<script setup lang="ts">
	import { registerSW } from 'virtual:pwa-register'

	const updateAvailable = ref(false)
	const isUpdating = ref(false)
	let updateServiceWorker: (() => Promise<void>) | undefined
	let registration: ServiceWorkerRegistration | undefined

	const checkForUpdate = () => {
		if (navigator.onLine && document.visibilityState === 'visible') {
			registration?.update().catch(() => {
				// A failed update check should not interrupt the current app session.
			})
		}
	}

	const applyUpdate = async () => {
		if (!updateServiceWorker || isUpdating.value) return

		isUpdating.value = true
		await updateServiceWorker()
	}

	onMounted(() => {
		updateServiceWorker = registerSW({
			immediate: true,
			onNeedRefresh() {
				updateAvailable.value = true
			},
			onNeedReload() {
				window.location.reload()
			},
			onRegisteredSW(
				_serviceWorkerUrl: unknown,
				serviceWorkerRegistration: ServiceWorkerRegistration | undefined
			) {
				registration = serviceWorkerRegistration
				window.addEventListener('online', checkForUpdate)
				document.addEventListener('visibilitychange', checkForUpdate)
			},
			onRegisterError(error) {
				console.error('PWA service worker registration failed:', error)
			}
		})
	})

	onBeforeUnmount(() => {
		window.removeEventListener('online', checkForUpdate)
		document.removeEventListener('visibilitychange', checkForUpdate)
	})
</script>

<template>
	<dialogs-error
		v-model:show="updateAvailable"
		:use-cancel-btn="false"
		:close-on-bg="false"
		modal-class="!max-w-[420px] !min-h-[auto] !rounded-[28px] !px-6 !py-7 md:!px-8"
	>
		<template #icon>
			<lazy-tools-icon
				icon="alarm"
				dir="media"
				:height="30"
				:width="30"
				class="text-primary-600"
				path-class="fill-primary-2"
			/>
		</template>

		<template #p>
			<div class="text-center">
				<h2 class="text-xl font-semibold text-blue-950">A new version is ready</h2>
				<p class="mt-2 text-sm leading-6 text-gray-600">
					Update now to get the latest improvements. Save any unsaved changes before continuing.
				</p>
			</div>
		</template>

		<template #button>
			<button
				type="button"
				class="default-btn mt-7 w-full justify-center rounded-xl bg-primary-2 py-3 text-sm font-semibold shadow-sm transition hover:bg-primary-500 disabled:cursor-wait disabled:opacity-70"
				:disabled="isUpdating"
				@click="applyUpdate"
			>
				{{ isUpdating ? 'Updating…' : 'Update app' }}
			</button>
		</template>
	</dialogs-error>
</template>
